import React, { useCallback, useMemo, useState, useEffect, useRef } from "react";
import { Input, Alert } from "antd";
import { debounce } from 'lodash';

const SearchAddress = ({ onAddressChange, address, fieldName, disabled = false }) => {
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const autocompleteService = useRef(null);
  const placesService = useRef(null);

  // Memoize the placeholder text
  const placeholderText = useMemo(() => {
    if (disabled) return "Recherche d'adresse désactivée";
    return "Entrez une ville ou une adresse";
  }, [disabled]);

  // Initialize input value from address prop
  useEffect(() => {
    if (address) {
      setInputValue(address.formatted_address || '');
    } else {
      setInputValue('');
    }
  }, [address]);

  // Initialize Google services
  useEffect(() => {
    if (disabled || !window.google) return;

    try {
      autocompleteService.current = new window.google.maps.places.AutocompleteService();
      placesService.current = new window.google.maps.places.PlacesService(
        document.createElement('div')
      );
    } catch (err) {
      console.error('Error initializing Google services:', err);
      setError("Erreur lors de l'initialisation des services Google");
    }
  }, [disabled]);

  // Debounced search function
  const searchPlaces = useCallback(
    debounce(async (input) => {
      if (!input.trim() || !autocompleteService.current) {
        setSuggestions([]);
        return;
      }

      try {
        const request = {
          input: input,
          types: ['geocode', 'establishment'],
          componentRestrictions: { country: 'fr' },
        };

        autocompleteService.current.getPlacePredictions(request, (predictions, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
            setSuggestions(predictions);
          } else {
            setSuggestions([]);
          }
        });
      } catch (err) {
        console.error('Error searching places:', err);
        setSuggestions([]);
      }
    }, 300),
    []
  );

  const handleInputChange = useCallback((e) => {
    setError(null);
    const value = e.target.value;
    setInputValue(value);
    setShowSuggestions(true);
    
    if (!value.trim()) {
      onAddressChange(fieldName, null);
      setSuggestions([]);
      return;
    }

    searchPlaces(value);
    onAddressChange(fieldName, { formatted_address: value });
  }, [onAddressChange, fieldName, searchPlaces]);

  const handleSuggestionClick = useCallback((prediction) => {
    setIsLoading(true);
    setError(null);
    setShowSuggestions(false);
    setInputValue(prediction.description);

    if (!placesService.current) {
      setIsLoading(false);
      return;
    }

    const request = {
      placeId: prediction.place_id,
      fields: ['formatted_address', 'geometry', 'name', 'place_id']
    };

    placesService.current.getDetails(request, (place, status) => {
      setIsLoading(false);
      
      if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
        const formattedPlace = {
          ...place,
          geoFormat: {
            type: 'Point',
            coordinates: [
              place.geometry.location.lng(), 
              place.geometry.location.lat()
            ]
          }
        };

        onAddressChange(fieldName, formattedPlace);
      } else {
        setError("Erreur lors de la récupération des détails de l'adresse");
      }
    });
  }, [onAddressChange, fieldName]);

  return (
    <div className="search-address-container relative">
      {error && (
        <Alert
          message="Erreur"
          description={error}
          type="error"
          showIcon
          closable
          onClose={() => setError(null)}
          className="mb-2"
        />
      )}
      
      <Input
        size="large"
        value={inputValue}
        placeholder={placeholderText}
        onChange={handleInputChange}
        onFocus={() => setShowSuggestions(true)}
        disabled={disabled}
        loading={isLoading}
        allowClear
        className="search-address-input"
        suffix={
          isLoading ? (
            <div className="animate-spin w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full" />
          ) : null
        }
      />

      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
          {suggestions.map((suggestion) => (
            <div
              key={suggestion.place_id}
              className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm"
              onClick={() => handleSuggestionClick(suggestion)}
            >
              {suggestion.description}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SearchAddress;